
import React, { useEffect, useState } from "react";
import {
  Routes,
  Route,
  useLocation,
  Navigate,
  useNavigate,
  Link,
} from "react-router-dom";

import { useDispatch, useSelector } from "react-redux";

import {
  Login,
} from "./pages/index.js";

import { ENUM_USER_TYPE, linksUrl } from "./constants/enum.js";
import PrivateRoute from "./PrivateRoute.jsx";

import {
  SideNav,
  Nav,
} from "./components/index.js";

import { Drawer, Button } from "@mui/material";

import {
  faHomeUser,
  faListCheck,
  faSquareXmark,
  faTruckArrowRight,
  faWarning,
} from "@fortawesome/free-solid-svg-icons";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast, ToastContainer } from "react-toastify";

import { menuPages } from "./constants/menuPages.js";
import usePagesTitle from "./constants/pagesTitle.js";

import { resetCompCode } from "./state/companySlice.jsx";
import { setLogout } from "./state/authSlice.jsx";
import {
  setDetailParams,
  setNewEntryMode,
} from "./state/orderSlice.jsx";

import { isMobile } from "react-device-detect";

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    isAuthenticated,
    tokenExpiry,
    user,
  } = useSelector((state) => state.auth);

  const [openDrawer, setOpenDrawer] = useState(false);

  // Match the role property used in Nav.jsx and SideNav.jsx.
  const userRole = String(user?.userType ?? "").trim();

  const isDistributor = userRole === ENUM_USER_TYPE.DISTRIBUTOR;

  // Distributor: slate / steel-blue.
  // Sales Person: red.
  const appTheme = isDistributor
    ? {
        page:
          "bg-gradient-to-b from-violet-100 via-violet-50 to-violet-100",
        footer:
          "border-violet-400 bg-gradient-to-r from-violet-800 to-violet-800",
        footerButton:
          "border-slate-400 hover:bg-blue-700",
        footerIcon: "text-white",
        warningIcon: "text-violet-700",
      }
    : {
        page:
          "bg-gradient-to-b from-red-50 via-gray-50 to-rose-100",
        footer:
          "border-red-500 bg-gradient-to-r from-red-800 to-red-700",
        footerButton:
          "border-red-300 hover:bg-red-600",
        footerIcon: "text-white",
        warningIcon: "text-rose-500",
      };

  // Token expiry notification.
  useEffect(() => {
    if (!tokenExpiry) return;

    const currentTime = Date.now() / 1000;

    if (currentTime > tokenExpiry) {
      if (
        !toast.isActive("tknExpiremsg") &&
        !toast.isActive("tknExpire") &&
        !toast.isActive("toast_tkexp") &&
        !toast.isActive("toast_tknf")
      ) {
        toast.error("Session expired. Please log in again.", {
          position: "top-center",
          toastId: "tknExpire",
        });
      }
    }
  }, [tokenExpiry]);

  // Redirect unauthenticated users.
  useEffect(() => {
    if (!isAuthenticated) {
      navigate(linksUrl.login);
    }
  }, [isAuthenticated, navigate]);

  const handleExpiredDrawerClose = () => {
    setOpenDrawer(false);
    dispatch(setLogout());
    dispatch(resetCompCode());
    navigate(linksUrl.login);
  };

  const handleNewOrder = () => {
    dispatch(setNewEntryMode(true));
    dispatch(setDetailParams([]));
    navigate(linksUrl.salesOrder.createEntry);
  };

  const shouldShowSidebar = () => {
    const { pathname } = location;

    return ![
      "/login",
      "/signup",
      "/sidenav",
    ].includes(pathname);
  };

  usePagesTitle();

  return (
    <div>
      <ToastContainer
        className="toast-container"
        toastClassName="dark-toast"
        progressClassName="progress-toast"
      />

      <div
        className={`relative min-h-screen transition-colors duration-300 ${appTheme.page}`}
      >
        {/* Token expiry drawer */}
        <Drawer
          open={openDrawer}
          anchor="top"
          onClose={() => setOpenDrawer(false)}
        >
          <button
            type="button"
            aria-label="Close session expiry notice"
            onClick={handleExpiredDrawerClose}
            className="absolute right-0 top-0 cursor-pointer p-3 text-3xl text-slate-700"
          >
            <FontAwesomeIcon icon={faSquareXmark} />
          </button>

          <div className="border-b-2 border-slate-300 p-8 text-center font-semibold text-slate-800">
            <FontAwesomeIcon
              className={`mr-2 ${appTheme.warningIcon}`}
              icon={faWarning}
            />
            Session Has Expired!
            <p className="mt-2 text-sm font-normal text-slate-600">
              Please log in again to continue.
            </p>
          </div>
        </Drawer>

        {/* Navigation */}
        {shouldShowSidebar() && <Nav />}
        {shouldShowSidebar() && <SideNav />}

        {/* Application routes */}
        <Routes>
          <Route
            path={linksUrl.login}
            element={
              !isAuthenticated ? (
                <Login />
              ) : (
                <Navigate to={linksUrl.companies} />
              )
            }
          />

          {menuPages.map((page, index) => (
            <Route
              key={index}
              path={page.path}
              element={
                <PrivateRoute page={page}>
                  {page.component}
                </PrivateRoute>
              }
            />
          ))}
        </Routes>

        {/* Mobile footer */}
        {shouldShowSidebar() && isMobile && (
          <div id="footer">
            <div className="mx-auto">
              <div
                className={`
                  fixed bottom-0 left-0 z-[99]
                  flex h-14 w-full items-center justify-center
                  border-t-2 p-2
                  ${appTheme.footer}
                `}
              >
                <div className="mx-auto flex items-center justify-start gap-4">
                  <Link to={linksUrl.companies}>
                    <Button
                      size="md"
                      variant="outlined"
                      aria-label="Home"
                      className={appTheme.footerButton}
                      sx={{
                        minWidth: 42,
                        width: 42,
                        height: 42,
                        borderColor: "rgba(255,255,255,0.4)",
                      }}
                    >
                      <FontAwesomeIcon
                        className={`text-xl ${appTheme.footerIcon}`}
                        icon={faHomeUser}
                      />
                    </Button>
                  </Link>

                  <Button
                    size="md"
                    aria-label="New Order"
                    onClick={handleNewOrder}
                    variant="outlined"
                    className={appTheme.footerButton}
                    sx={{
                      minWidth: 42,
                      width: 42,
                      height: 42,
                      borderColor: "rgba(255,255,255,0.4)",
                    }}
                  >
                    <FontAwesomeIcon
                      className={`text-xl ${appTheme.footerIcon}`}
                      icon={faTruckArrowRight}
                    />
                  </Button>

                  <Link to={linksUrl.salesOrder.orderMaster}>
                    <Button
                      size="md"
                      aria-label="Order List"
                      variant="outlined"
                      className={appTheme.footerButton}
                      sx={{
                        minWidth: 42,
                        width: 42,
                        height: 42,
                        borderColor: "rgba(255,255,255,0.4)",
                      }}
                    >
                      <FontAwesomeIcon
                        className={`text-xl ${appTheme.footerIcon}`}
                        icon={faListCheck}
                      />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;